"use client";

import { useCallback, useState } from "react";
import type { ProviderName } from "@/lib/payments/types";
import type { ReserveErrorCode } from "@/lib/cart/cart-reserve";

export interface ValidationError {
  errorCode: ReserveErrorCode;
  titles: string[];
}

/**
 * Minimal per-item input the gate needs to answer "can this seller be paid?".
 *
 * `availableProviders` is REQUIRED on purpose. The gate cannot make a
 * decision without it, so making it required means any caller that forgets
 * to source it — e.g. the Buy Now / chat quick-checkout path in
 * `use-checkout-session.ts`, which hand-builds these objects from a raw
 * `/api/cart` row — fails to COMPILE instead of silently constructing an
 * object with `availableProviders === undefined` and getting false-blocked
 * in production. (That regression is exactly what shipped when the gate was
 * migrated off `paypalMerchantId` but this call site kept casting `as any`.)
 *
 * `[]` = the server resolved no working rails for this seller → blocked.
 * Deliberately narrower than `CartItemData`: the gate is per-seller, and a
 * wide UI type invites callers to fabricate partial cart items.
 */
export interface SellerAvailabilityCheck {
  sellerId: string;
  availableProviders: ProviderName[];
  /** Listing title — surfaced in the block modal. */
  name: string;
}

export interface PreCheckoutValidationResult {
  isValidating: boolean;
  validationError: ValidationError | null;
  invalidSellerIds: Set<string>;
  sellerValidationErrors: Record<string, string>;
  validate: (
    items: SellerAvailabilityCheck[],
  ) => Promise<{ valid: boolean; error?: ValidationError }>;
  clearError: () => void;
}

/**
 * Pre-checkout gate: every seller in the cart must have at least one
 * available payment rail (`item.availableProviders.length > 0`).
 *
 * Source of truth lives on the server (`getAvailableProvidersForMany` in
 * lib/payments/router.ts). The cart fetch (`/api/cart`) already bakes
 * `availableProviders` into each item, so this hook does not need to make
 * an additional network call — it inspects what the server already told us.
 *
 * Async signature is preserved for backward compatibility with the original
 * `validateSellerMerchants`-based hook.
 *
 * See `.tondocs/todos/payments/p1-rails-aware-pre-checkout-validation.md`
 * for the rail-aware migration context (and why this hook no longer reads
 * `paypalMerchantId` for gating).
 */
export function usePreCheckoutValidation(): PreCheckoutValidationResult {
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<ValidationError | null>(null);
  const [invalidSellerIds, setInvalidSellerIds] = useState<Set<string>>(new Set());
  const [sellerValidationErrors, setSellerValidationErrors] = useState<
    Record<string, string>
  >({});

  const clearError = useCallback(() => {
    setValidationError(null);
    setInvalidSellerIds(new Set());
    setSellerValidationErrors({});
  }, []);

  const validate = useCallback(
    async (items: SellerAvailabilityCheck[]) => {
      setIsValidating(true);
      setInvalidSellerIds(new Set());
      setSellerValidationErrors({});
      setValidationError(null);

      try {
        // Per-seller decision: a seller is "available" iff at least one rail
        // accepts their checkout. The list is computed on the server
        // (getAvailableProvidersForMany) and threaded in by the caller; an
        // empty list means the server resolved no working rails → blocked.
        const sellerStatus = new Map<string, boolean>();
        items.forEach((item) => {
          if (!item.sellerId) return;
          if (sellerStatus.has(item.sellerId)) return;
          sellerStatus.set(item.sellerId, item.availableProviders.length > 0);
        });

        const invalidIds = new Set<string>();
        const errors: Record<string, string> = {};
        sellerStatus.forEach((ok, sellerId) => {
          if (!ok) {
            invalidIds.add(sellerId);
            errors[sellerId] = "Seller cannot accept payments";
          }
        });

        if (invalidIds.size > 0) {
          const affected = items.filter((i) => invalidIds.has(i.sellerId));
          const error: ValidationError = {
            errorCode: "SELLER_PAYMENT_ERROR",
            titles: affected.map((i) => i.name),
          };
          setInvalidSellerIds(invalidIds);
          setSellerValidationErrors(errors);
          setValidationError(error);
          return { valid: false, error };
        }

        return { valid: true };
      } finally {
        setIsValidating(false);
      }
    },
    [],
  );

  return {
    isValidating,
    validationError,
    invalidSellerIds,
    sellerValidationErrors,
    validate,
    clearError,
  };
}
